import { createUnifiedTheme, genPageTheme, palettes } from '@backstage/theme';
import { type VerdantSemanticColors, verdantTokens } from './verdantTokens';

const light = verdantTokens.modes.light.semantic;
const dark = verdantTokens.modes.dark.semantic;
const navigation = {
  ...verdantTokens.navigation,
  navItem: { hoverBackground: verdantTokens.navigation.hoverBackground },
  submenu: { background: verdantTokens.navigation.submenuBackground },
};

const createPageThemes = (background: string, fontColor: string) => {
  const page = genPageTheme({
    colors: [background],
    shape: 'none',
    options: { fontColor },
  });
  return {
    home: page,
    documentation: page,
    tool: page,
    service: page,
    website: page,
    library: page,
    other: page,
    app: page,
    apis: page,
    card: page,
  };
};

const colorCompatibility = (colors: VerdantSemanticColors) => ({
  MuiCssBaseline: {
    styleOverrides: {
      html: { backgroundColor: colors.app },
      body: { backgroundColor: colors.app, color: colors.text },
      '::selection': {
        backgroundColor: colors.primarySoft,
        color: colors.text,
      },
      ':focus-visible': { outlineColor: colors.focus },
    },
  },
});

export const verdantLightTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: {
      main: light.primary,
      dark: light.primaryHover,
      light: light.focus,
      contrastText: light.primaryContrast,
    },
    secondary: { main: light.info, contrastText: light.primaryContrast },
    background: { default: light.app, paper: light.surface },
    text: {
      primary: light.text,
      secondary: light.muted,
      disabled: light.disabled,
    },
    divider: light.border,
    border: light.border,
    textContrast: light.text,
    textVerySubtle: light.disabled,
    textSubtle: light.muted,
    highlight: light.primarySoft,
    errorBackground: light.criticalSoft,
    warningBackground: light.warningSoft,
    infoBackground: light.infoSoft,
    errorText: light.critical,
    warningText: light.warning,
    infoText: light.info,
    link: light.primary,
    linkHover: light.primaryHover,
    navigation,
    tabbar: { indicator: light.primary },
    pinSidebarButton: { icon: '#D8E5DE', background: '#17241F' },
    status: {
      ok: light.primary,
      warning: light.warning,
      error: light.critical,
      pending: light.muted,
      running: light.info,
      aborted: light.disabled,
    },
    banner: {
      info: light.info,
      warning: light.warning,
      error: light.critical,
      text: '#FFFFFF',
      link: '#FFFFFF',
      closeButtonColor: '#FFFFFF',
    },
    code: { background: light.raised },
  },
  defaultPageTheme: 'home',
  pageTheme: createPageThemes(light.raised, light.text),
  components: colorCompatibility(light),
});

export const verdantDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: {
      main: dark.primary,
      dark: '#2CB980',
      light: dark.primaryHover,
      contrastText: dark.primaryContrast,
    },
    secondary: { main: dark.info, contrastText: dark.app },
    background: { default: dark.app, paper: dark.surface },
    text: {
      primary: dark.text,
      secondary: dark.muted,
      disabled: dark.disabled,
    },
    divider: dark.border,
    border: dark.border,
    textContrast: dark.text,
    textVerySubtle: dark.disabled,
    textSubtle: dark.muted,
    highlight: dark.primarySoft,
    errorBackground: dark.criticalSoft,
    warningBackground: dark.warningSoft,
    infoBackground: dark.infoSoft,
    errorText: dark.critical,
    warningText: dark.warning,
    infoText: dark.info,
    link: dark.primary,
    linkHover: dark.primaryHover,
    navigation,
    tabbar: { indicator: dark.primary },
    pinSidebarButton: { icon: '#D8E5DE', background: '#17241F' },
    status: {
      ok: dark.primary,
      warning: dark.warning,
      error: dark.critical,
      pending: dark.muted,
      running: dark.info,
      aborted: dark.disabled,
    },
    banner: {
      info: '#245CA8',
      warning: '#8A5D10',
      error: '#A43D48',
      text: '#FFFFFF',
      link: '#FFFFFF',
      closeButtonColor: '#FFFFFF',
    },
    code: { background: dark.raised },
  },
  defaultPageTheme: 'home',
  pageTheme: createPageThemes(dark.raised, dark.text),
  components: colorCompatibility(dark),
});
